import { useState, useRef, useCallback, useEffect } from 'react';
import {
    PCM_TARGET_SAMPLE_RATE,
    concatInt16Arrays,
    downsampleToRate,
    encodeWavFromInt16,
    floatTo16BitPCM,
    mixToMono
} from 'utils/pcmWav';

export const MAX_VOICE_RECORDING_DURATION_MS = 15 * 60 * 1000; // 15 minutes

export enum VoiceRecorderState {
    IDLE = 'IDLE',
    RECORDING_AUDIO = 'RECORDING_AUDIO',
    RECORDING_PAUSED = 'RECORDING_PAUSED',
    RECORD_PLAYING = 'RECORD_PLAYING',
    RECORD_PAUSED = 'RECORD_PAUSED'
}

export interface VoiceRecorderStateData {
    currentState: VoiceRecorderState;
    isRecording: boolean;
    isPaused: boolean;
    duration: number;
    formattedDuration: string;
    audioLevel: number;
    isPlaying: boolean;
    playbackDuration: number;
    formattedPlaybackDuration: string;
    audioUrl: string | null;
    hasReachedDurationLimit: boolean;
}

export interface VoiceRecorderActions {
    startRecording: () => Promise<void>;
    stopRecording: () => void;
    cancelRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
    playRecording: () => void;
    pausePlayback: () => void;
    stopPlayback: () => void;
    seekTo: (time: number) => void;
    getAudioData: () => Promise<{audioBlob: Blob, duration: number}>;
}

export interface UseVoiceRecorderProps {
    onRecordingComplete?: (audioBlob: Blob, duration: number) => void;
    onRecordingStart?: () => void;
    onRecordingStop?: () => void;
    onRecordingCancel?: () => void;
    onRecordingPause?: () => void;
    onRecordingResume?: () => void;
}

const PROCESSOR_BUFFER_SIZE = 4096;

export const useVoiceRecorder = ({
    onRecordingComplete,
    onRecordingStart,
    onRecordingStop,
    onRecordingCancel,
    onRecordingPause,
    onRecordingResume
}: UseVoiceRecorderProps = {}): [VoiceRecorderStateData, VoiceRecorderActions] => {
    const [currentState, setCurrentState] = useState<VoiceRecorderState>(VoiceRecorderState.IDLE);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [duration, setDuration] = useState(0);
    const [audioLevel, setAudioLevel] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackDuration, setPlaybackDuration] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [hasReachedDurationLimit, setHasReachedDurationLimit] = useState(false);

    const mediaStreamRef = useRef<MediaStream | null>(null);
    const isStartingRecordingRef = useRef<boolean>(false);
    const isCancellingRecordingRef = useRef<boolean>(false);
    const isSessionActiveRef = useRef<boolean>(false);
    const recordingStartGenerationRef = useRef(0);
    const pcmChunksRef = useRef<Int16Array[]>([]);
    const startTimeRef = useRef<number>(0);
    const pausedTimeRef = useRef<number>(0);
    const totalPausedTimeRef = useRef<number>(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const isPausedRef = useRef<boolean>(false);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const durationLimitReachedRef = useRef<boolean>(false);
    const pauseRecordingRef = useRef<(() => void) | null>(null);
    const getRecordingDurationMs = useCallback(() => {
        return Math.min(
            Date.now() - startTimeRef.current - totalPausedTimeRef.current,
            MAX_VOICE_RECORDING_DURATION_MS
        );
    }, []);

    const formatDuration = useCallback((ms: number): string => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }, []);

    const checkDurationLimit = useCallback((elapsedTime: number) => {
        if (durationLimitReachedRef.current) return;
        if (elapsedTime >= MAX_VOICE_RECORDING_DURATION_MS) {
            durationLimitReachedRef.current = true;
            setHasReachedDurationLimit(true);
            setDuration(MAX_VOICE_RECORDING_DURATION_MS);
            pauseRecordingRef.current?.();
        }
    }, []);

    const createAudioBlob = useCallback(() => {
        const samples = concatInt16Arrays(pcmChunksRef.current);
        return encodeWavFromInt16(samples, PCM_TARGET_SAMPLE_RATE);
    }, []);

    const resetAudioElement = useCallback(() => {
        if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current.src = '';
            audioElementRef.current = null;
        }
        if (playbackIntervalRef.current) {
            clearInterval(playbackIntervalRef.current);
            playbackIntervalRef.current = null;
        }
    }, []);

    const createAudioUrlFromCurrentChunks = useCallback(() => {
        if (pcmChunksRef.current.length > 0) {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }

            resetAudioElement();
            const audioBlob = createAudioBlob();
            const url = URL.createObjectURL(audioBlob);
            setAudioUrl(url);
            return url;
        }
        return null;
    }, [audioUrl, createAudioBlob, resetAudioElement]);

    const disconnectCaptureGraph = useCallback(() => {
        if (processorRef.current) {
            processorRef.current.onaudioprocess = null;
            try {
                processorRef.current.disconnect();
            } catch {
                // already disconnected
            }
            processorRef.current = null;
        }
        if (sourceNodeRef.current) {
            try {
                sourceNodeRef.current.disconnect();
            } catch {
                // already disconnected
            }
            sourceNodeRef.current = null;
        }
        analyserRef.current = null;
    }, []);

    const closeAudioContext = useCallback(() => {
        disconnectCaptureGraph();
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            void audioContextRef.current.close();
            audioContextRef.current = null;
        }
    }, [disconnectCaptureGraph]);

    const cleanupMediaStream = useCallback(() => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
    }, []);

    const finalizeRecordingSession = useCallback((options?: { invokeComplete?: boolean }) => {
        const invokeComplete = options?.invokeComplete ?? false;
        const wasCancelled = isCancellingRecordingRef.current;

        disconnectCaptureGraph();
        cleanupMediaStream();
        closeAudioContext();
        isSessionActiveRef.current = false;

        if (wasCancelled) {
            pcmChunksRef.current = [];
            isCancellingRecordingRef.current = false;
            return null;
        }

        if (pcmChunksRef.current.length === 0) {
            console.error('[Audio] No audio chunks recorded');
            isCancellingRecordingRef.current = false;
            return null;
        }

        const audioBlob = createAudioBlob();
        const recordingDuration = getRecordingDurationMs();

        if (invokeComplete && onRecordingComplete) {
            onRecordingComplete(audioBlob, recordingDuration);
        }

        isCancellingRecordingRef.current = false;
        return { audioBlob, duration: recordingDuration };
    }, [
        cleanupMediaStream,
        closeAudioContext,
        createAudioBlob,
        disconnectCaptureGraph,
        getRecordingDurationMs,
        onRecordingComplete
    ]);

    const clearRecordingUiTimers = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    const resetRecordingUiState = useCallback(() => {
        setIsRecording(false);
        setIsPaused(false);
        setCurrentState(VoiceRecorderState.IDLE);
        isPausedRef.current = false;
        setDuration(0);
        setAudioLevel(0);
        clearRecordingUiTimers();
    }, [clearRecordingUiTimers]);

    const seekTo = useCallback((time: number) => {
        if (audioElementRef.current) {
            const wasPlaying = !audioElementRef.current.paused;

            audioElementRef.current.currentTime = time / 1000;
            setPlaybackDuration(time);

            if (wasPlaying) {
                void audioElementRef.current.play();
            }

            setCurrentState(wasPlaying ? VoiceRecorderState.RECORD_PLAYING : VoiceRecorderState.RECORD_PAUSED);

            if (playbackIntervalRef.current) {
                clearInterval(playbackIntervalRef.current);
            }
            playbackIntervalRef.current = setInterval(() => {
                if (audioElementRef.current) {
                    setPlaybackDuration(audioElementRef.current.currentTime * 1000);
                }
            }, 100);
        }
    }, []);

    const playRecording = useCallback(() => {
        if (!audioUrl && pcmChunksRef.current.length === 0) return;

        const url = audioUrl || createAudioUrlFromCurrentChunks();
        if (!url) return;

        resetAudioElement();
        audioElementRef.current = new Audio(url);
        audioElementRef.current.onended = () => {
            setIsPlaying(false);
            setCurrentState(VoiceRecorderState.RECORD_PAUSED);
        };
        audioElementRef.current.ontimeupdate = () => {
            setPlaybackDuration(
                audioElementRef.current?.currentTime
                    ? audioElementRef.current.currentTime * 1000
                    : 0
            );
        };

        audioElementRef.current.play().catch(error => {
            if (error.name !== 'AbortError') {
                console.error('Error playing audio:', error);
                setIsPlaying(false);
                setCurrentState(VoiceRecorderState.RECORD_PAUSED);
            }
        });

        setIsPlaying(true);
        setCurrentState(VoiceRecorderState.RECORD_PLAYING);
    }, [audioUrl, createAudioUrlFromCurrentChunks, resetAudioElement]);

    const pausePlayback = useCallback(() => {
        if (audioElementRef.current) {
            audioElementRef.current.pause();
            setIsPlaying(false);
            setCurrentState(VoiceRecorderState.RECORD_PAUSED);
        }
    }, []);

    const stopPlayback = useCallback(() => {
        resetAudioElement();
        setIsPlaying(false);
        setPlaybackDuration(0);
        setCurrentState(VoiceRecorderState.IDLE);
    }, [resetAudioElement]);

    const updateAudioLevel = useCallback(() => {
        if (!analyserRef.current || isPausedRef.current) {
            setAudioLevel(0);
            return;
        }

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        const sum = dataArray.reduce((acc, val) => acc + val, 0);
        const average = sum / dataArray.length;
        const normalizedLevel = Math.pow(average / 255, 1.5);

        setAudioLevel(normalizedLevel);

        if (isRecording && !isPausedRef.current) {
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
    }, [isRecording]);

    const appendPcmFromInputBuffer = useCallback((inputBuffer: AudioBuffer) => {
        if (isPausedRef.current || isCancellingRecordingRef.current) {
            return;
        }

        const channelCount = inputBuffer.numberOfChannels;
        const frameCount = inputBuffer.length;
        const interleaved = new Float32Array(frameCount * channelCount);

        for (let channel = 0; channel < channelCount; channel++) {
            const channelData = inputBuffer.getChannelData(channel);
            for (let i = 0; i < frameCount; i++) {
                interleaved[i * channelCount + channel] = channelData[i];
            }
        }

        const mono = mixToMono(interleaved, channelCount);
        const contextRate = audioContextRef.current?.sampleRate || inputBuffer.sampleRate;
        const downsampled = downsampleToRate(mono, contextRate, PCM_TARGET_SAMPLE_RATE);
        const pcm = floatTo16BitPCM(downsampled);

        if (pcm.length > 0) {
            pcmChunksRef.current.push(pcm);
        }
    }, []);

    const startRecording = useCallback(async () => {
        let stream: MediaStream | null = null;
        const startGeneration = recordingStartGenerationRef.current + 1;

        try {
            if (
                isStartingRecordingRef.current ||
                isSessionActiveRef.current
            ) {
                return;
            }

            isStartingRecordingRef.current = true;
            recordingStartGenerationRef.current = startGeneration;
            isCancellingRecordingRef.current = false;

            pcmChunksRef.current = [];
            durationLimitReachedRef.current = false;
            setHasReachedDurationLimit(false);
            resetAudioElement();
            closeAudioContext();
            cleanupMediaStream();
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
            }

            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1
                }
            });
            mediaStreamRef.current = stream;

            if (recordingStartGenerationRef.current !== startGeneration) {
                stream.getTracks().forEach(track => track.stop());
                if (mediaStreamRef.current === stream) {
                    mediaStreamRef.current = null;
                }
                return;
            }

            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;

            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            const source = audioContext.createMediaStreamSource(stream);
            sourceNodeRef.current = source;

            const analyser = audioContext.createAnalyser();
            analyserRef.current = analyser;

            // ScriptProcessor mirrors RecordRTC StereoAudioRecorder capture path:
            // pull raw samples, resample to 16 kHz mono, later wrap as WAV.
            const processor = audioContext.createScriptProcessor(
                PROCESSOR_BUFFER_SIZE,
                1,
                1
            );
            processorRef.current = processor;

            processor.onaudioprocess = (event) => {
                appendPcmFromInputBuffer(event.inputBuffer);
            };

            const silentGain = audioContext.createGain();
            silentGain.gain.value = 0;

            source.connect(analyser);
            source.connect(processor);
            // Keep processor alive in the graph (required in Chromium) without audible monitor
            processor.connect(silentGain);
            silentGain.connect(audioContext.destination);

            isSessionActiveRef.current = true;
            setIsRecording(true);
            setIsPaused(false);
            setCurrentState(VoiceRecorderState.RECORDING_AUDIO);
            isPausedRef.current = false;
            startTimeRef.current = Date.now();
            totalPausedTimeRef.current = 0;

            intervalRef.current = setInterval(() => {
                if (!isPausedRef.current) {
                    const elapsedTime = getRecordingDurationMs();
                    setDuration(elapsedTime);
                    checkDurationLimit(elapsedTime);
                }
            }, 50);

            updateAudioLevel();
            onRecordingStart?.();
        } catch (error) {
            console.error('Error starting recording:', error);
            stream?.getTracks().forEach(track => track.stop());
            if (mediaStreamRef.current === stream) {
                mediaStreamRef.current = null;
            }
            isSessionActiveRef.current = false;
            closeAudioContext();
        } finally {
            if (recordingStartGenerationRef.current === startGeneration) {
                isStartingRecordingRef.current = false;
            }
        }
    }, [
        appendPcmFromInputBuffer,
        audioUrl,
        checkDurationLimit,
        cleanupMediaStream,
        closeAudioContext,
        getRecordingDurationMs,
        onRecordingStart,
        resetAudioElement,
        updateAudioLevel
    ]);

    const pauseRecording = useCallback(() => {
        if (!isSessionActiveRef.current || isPausedRef.current) {
            return;
        }

        const pauseTime = Date.now();
        const exactDuration = Math.min(
            pauseTime - startTimeRef.current - totalPausedTimeRef.current,
            MAX_VOICE_RECORDING_DURATION_MS
        );
        setDuration(exactDuration);

        setIsPaused(true);
        setCurrentState(VoiceRecorderState.RECORDING_PAUSED);
        isPausedRef.current = true;
        pausedTimeRef.current = pauseTime;

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        createAudioUrlFromCurrentChunks();
        onRecordingPause?.();
    }, [createAudioUrlFromCurrentChunks, onRecordingPause]);

    pauseRecordingRef.current = pauseRecording;

    const resumeRecording = useCallback(() => {
        if (durationLimitReachedRef.current || !isSessionActiveRef.current || !isPausedRef.current) {
            return;
        }

        const resumeTime = Date.now();
        const pauseDuration = resumeTime - pausedTimeRef.current;
        totalPausedTimeRef.current += pauseDuration;

        isPausedRef.current = false;
        setIsPaused(false);
        setCurrentState(VoiceRecorderState.RECORDING_AUDIO);

        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }
        stopPlayback();

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        setTimeout(() => {
            const elapsedTime = getRecordingDurationMs();
            setDuration(elapsedTime);

            intervalRef.current = setInterval(() => {
                if (!isPausedRef.current) {
                    const nextElapsed = getRecordingDurationMs();
                    setDuration(nextElapsed);
                    checkDurationLimit(nextElapsed);
                }
            }, 50);
        }, 0);

        updateAudioLevel();
        onRecordingResume?.();
    }, [
        audioUrl,
        checkDurationLimit,
        getRecordingDurationMs,
        onRecordingResume,
        stopPlayback,
        updateAudioLevel
    ]);

    const stopRecording = useCallback(() => {
        if (!isSessionActiveRef.current) {
            return;
        }

        finalizeRecordingSession({ invokeComplete: true });
        createAudioUrlFromCurrentChunks();
        resetRecordingUiState();
        onRecordingStop?.();
    }, [
        createAudioUrlFromCurrentChunks,
        finalizeRecordingSession,
        onRecordingStop,
        resetRecordingUiState
    ]);

    const getAudioData = useCallback(() => {
        return new Promise<{audioBlob: Blob, duration: number}>((resolve, reject) => {
            try {
                if (isSessionActiveRef.current) {
                    const result = finalizeRecordingSession({ invokeComplete: false });
                    if (!result) {
                        reject(new Error('[Audio] No audio chunks recorded'));
                        return;
                    }

                    createAudioUrlFromCurrentChunks();
                    onRecordingStop?.();
                    resetRecordingUiState();
                    resolve(result);
                    return;
                }

                if (pcmChunksRef.current.length > 0) {
                    resolve({
                        audioBlob: createAudioBlob(),
                        duration
                    });
                    return;
                }

                reject(new Error('[Audio] No recording in progress and no audio data available'));
            } catch (error) {
                reject(error);
            }
        });
    }, [
        createAudioBlob,
        createAudioUrlFromCurrentChunks,
        duration,
        finalizeRecordingSession,
        onRecordingStop,
        resetRecordingUiState
    ]);

    const cancelRecording = useCallback(() => {
        recordingStartGenerationRef.current += 1;
        isStartingRecordingRef.current = false;
        isCancellingRecordingRef.current = true;

        if (isSessionActiveRef.current) {
            finalizeRecordingSession({ invokeComplete: false });
        } else {
            isCancellingRecordingRef.current = false;
            pcmChunksRef.current = [];
            cleanupMediaStream();
            closeAudioContext();
        }

        setIsRecording(false);
        setIsPaused(false);
        setCurrentState(VoiceRecorderState.IDLE);
        isPausedRef.current = false;
        durationLimitReachedRef.current = false;
        setHasReachedDurationLimit(false);
        setDuration(0);
        setAudioLevel(0);

        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }
        resetAudioElement();
        setIsPlaying(false);
        setPlaybackDuration(0);
        clearRecordingUiTimers();

        onRecordingCancel?.();
    }, [
        audioUrl,
        cleanupMediaStream,
        clearRecordingUiTimers,
        closeAudioContext,
        finalizeRecordingSession,
        onRecordingCancel,
        resetAudioElement
    ]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (playbackIntervalRef.current) {
                clearInterval(playbackIntervalRef.current);
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
            resetAudioElement();
            cleanupMediaStream();
            closeAudioContext();
        };
    }, [audioUrl, resetAudioElement, cleanupMediaStream, closeAudioContext]);

    const state: VoiceRecorderStateData = {
        currentState,
        isRecording,
        isPaused,
        duration,
        formattedDuration: formatDuration(duration),
        audioLevel,
        isPlaying,
        playbackDuration,
        formattedPlaybackDuration: formatDuration(playbackDuration),
        audioUrl,
        hasReachedDurationLimit
    };

    const actions: VoiceRecorderActions = {
        startRecording,
        stopRecording,
        cancelRecording,
        pauseRecording,
        resumeRecording,
        getAudioData,
        playRecording,
        pausePlayback,
        stopPlayback,
        seekTo
    };

    return [state, actions];
};
